import axios, { AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import * as HttpStatus from 'http-status-codes';
import {
  AUTH_STORAGE_KEY,
  AUTH_HEADER,
  AUTH_SCHEME,
  AUTH_PATHS,
  REFRESH_TOKEN_STORAGE_KEY,
} from '../features/auth/constants';
import {
  isKeycloakSession,
  refreshKeycloakSession,
  storeKeycloakSession,
} from '../features/auth/services/keycloakService';
import { emitTokenRefresh } from './sessionEventBus';
import config from '../config';

// Logger removed to avoid process.env dependencies in widget bundle

/**
 * Thin axios wrapper for a single API host, with shared JWT auth handling
 * (proactive + reactive token refresh) installed once across all instances.
 */
export class HttpRequest {
  private method: Method | undefined;
  private timeout: number | undefined;

  // Shared across all instances — prevents concurrent refresh race conditions
  private static refreshPromise: Promise<string> | null = null;
  // Interceptors are registered once on the global axios instance, not per HttpRequest instance
  private static interceptorsInstalled = false;
  private readonly initialHeaders = {
    accept: 'application/json',
    'content-type': 'application/json',
  };

  /**
   * @param host Base URL for requests made through this instance
   * @param responseType Optional axios responseType override (e.g. 'blob')
   */
  constructor(
    private host: string,
    private responseType: any = '',
  ) {
    HttpRequest.installInterceptors();
  }

  /**
   * GET request
   */
  async get(path: string = '', params: object = {}, headers: Record<string, string> = {}) {
    this.method = 'GET';
    return await this.request(path, params, headers);
  }

  /**
   * GET request with body
   */
  async getWithBody(path: string, params: object = {}, headers: Record<string, string> = {}) {
    this.method = 'GET';
    return await this.request(path, params, headers, true);
  }

  /**
   * POST request
   */
  async post(
    data: any = {},
    path: string = '',
    headers: Record<string, string> = this.initialHeaders,
    timeout: number = 0,
  ) {
    this.method = 'POST';
    this.timeout = timeout;
    return await this.request(path, data, headers);
  }

  /**
   * PUT request
   */
  async put(path: string, data: any = {}, headers: Record<string, string> = this.initialHeaders) {
    this.method = 'PUT';
    return await this.request(path, data, headers);
  }

  /**
   * PATCH request
   */
  async patch(path: string, data: any = {}, headers: Record<string, string> = this.initialHeaders) {
    this.method = 'PATCH';
    return await this.request(path, data, headers);
  }

  /**
   * DELETE request
   */
  async delete(
    path: string,
    data: object = {},
    headers: Record<string, string> = this.initialHeaders,
  ) {
    this.method = 'DELETE';
    return this.request(path, data, headers);
  }

  /**
   * Core request handler
   */
  async request(
    path: string,
    data: any,
    headers: Record<string, string>,
    isWithBody: boolean = false,
  ): Promise<AxiosResponse> {
    const config: AxiosRequestConfig = {
      method: this.method,
      url: !path ? this.host : `${this.host}/${path}`,
      headers: this.initialHeaders,
    };

    if (Object.keys(data).length) {
      const paramType: 'params' | 'data' = this.method === 'GET' && !isWithBody ? 'params' : 'data';
      config[paramType] = data;
    }

    if (Object.keys(headers).length) {
      config['headers'] = headers;
    }

    // Add JWT token to request headers if available
    const token = localStorage.getItem(AUTH_STORAGE_KEY);
    if (token && config.headers) {
      config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
    }

    if (this.responseType) {
      config['responseType'] = this.responseType;
    }

    if (this.timeout) {
      config['timeout'] = this.timeout;
    }

    return new Promise((resolve, reject) => {
      axios(config)
        .then((response: AxiosResponse) => {
          resolve(response);
        })
        .catch((error) => {
          // Los errores 401/403 ahora son manejados por el response interceptor
          // que incluye retry logic automático
          const errorResponse = error.response || { status: HttpStatus.INTERNAL_SERVER_ERROR };
          resolve(errorResponse);
        });
    });
  }

  /**
   * Registers the request/response interceptors on the global axios instance,
   * guarded so it only runs once no matter how many instances are constructed.
   */
  private static installInterceptors() {
    if (HttpRequest.interceptorsInstalled) {
      return;
    }
    HttpRequest.interceptorsInstalled = true;
    HttpRequest.setupRequestInterceptor();
    HttpRequest.setupResponseInterceptor();
  }

  /**
   * Setup request interceptor to check and refresh tokens before requests
   */
  private static setupRequestInterceptor() {
    const authEndpoint = `${config.endpoints.auth}/${AUTH_PATHS.LOGIN}`;
    axios.interceptors.request.use(
      async (config) => {
        // No agregar token ni intentar refresh en endpoints de autenticación
        const isAuthEndpoint = config.url?.includes(authEndpoint);

        if (isAuthEndpoint) {
          return config; // Dejar pasar sin modificar
        }

        const token = localStorage.getItem(AUTH_STORAGE_KEY);
        if (token) {
          // ✅ Renovación proactiva habilitada en todos los ambientes
          if (HttpRequest.isTokenNearExpiry(token)) {
            try {
              console.info('🔔 Token near expiry, proactively renewing...');
              const newToken = await HttpRequest.refreshAccessToken();
              config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${newToken}`;
              // Notify session manager about successful token refresh
              emitTokenRefresh(true, 'httpClient-proactive');
            } catch (error) {
              console.warn('⚠️ Error proactively renewing token:', error);
              // Notify session manager about failed token refresh
              emitTokenRefresh(false, 'httpClient-proactive');
              // Si falla el refresh, usar token actual y dejar que el response interceptor maneje el error
              config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
            }
          } else {
            config.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${token}`;
          }
        }
        return config;
      },
      (error) => Promise.reject(error),
    );
  }

  /**
   * Setup response interceptor with automatic retry logic for 401 errors only.
   * 403 (Forbidden) is a permission error — refreshing the token won't help.
   */
  private static setupResponseInterceptor() {
    axios.interceptors.response.use(
      (response) => response, // Peticiones exitosas pasan sin modificación
      async (error) => {
        const originalRequest = error.config;
        const authEndpoint = `${config.endpoints.auth}/${AUTH_PATHS.LOGIN}`;
        // No intentar refresh en endpoints de autenticación
        const isAuthEndpoint = originalRequest?.url?.includes(authEndpoint);

        // Solo hacer refresh en 401 (token inválido/expirado).
        // 403 = permiso denegado con token válido — el refresh no lo resuelve.
        if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
          originalRequest._retry = true; // Marcar para evitar loops infinitos

          // ✅ Intentar refresh automático tanto en desarrollo como en producción
          try {
            console.info('🔄 Token expired detected, attempting automatic renewal...');
            const newToken = await HttpRequest.refreshAccessToken();

            // Actualizar header de la petición original con el nuevo token
            originalRequest.headers[AUTH_HEADER] = `${AUTH_SCHEME} ${newToken}`;

            console.info('✅ Token renewed successfully, retrying original request...');
            // Notify session manager about successful token refresh
            emitTokenRefresh(true, 'httpClient-reactive');
            // Reintentar petición original con nuevo token
            return axios(originalRequest);
          } catch (refreshError) {
            console.error('❌ Error renewing token:', refreshError);
            // Notify session manager about failed token refresh
            emitTokenRefresh(false, 'httpClient-reactive');

            // Mostrar notificación informativa antes de redirigir
            import('antd')
              .then(({ notification }) => {
                notification.warning({
                  key: 'token-refresh-failed',
                  message: 'Session Expired',
                  description: 'Unable to renew your session automatically. Please login again.',
                  duration: 5,
                  placement: 'topRight',
                });
              })
              .catch(() => {
                console.info('Could not show token refresh failed notification');
              });

            // Limpiar tokens corruptos/expirados
            localStorage.removeItem(AUTH_STORAGE_KEY);
            localStorage.removeItem('refreshToken');

            // Solo redirigir si no estamos ya en login
            setTimeout(() => {
              if (!window.location.pathname.includes('login')) {
                console.info('Redirecting to login due to authentication error...');
                window.location.href = '/login';
              }
            }, 1000);

            return Promise.reject(refreshError);
          }
        }

        // Para otros errores o si ya se intentó refresh, rechazar normalmente
        return Promise.reject(error);
      },
    );
  }

  /**
   * Internal method to refresh access token using refresh token
   * Works in both development and production environments
   */
  private static async refreshAccessToken(): Promise<string> {
    // If a refresh is already in flight, wait for it instead of starting a new one.
    // This prevents race conditions when multiple concurrent requests all get 401.
    if (HttpRequest.refreshPromise) {
      return HttpRequest.refreshPromise;
    }

    HttpRequest.refreshPromise = HttpRequest.doRefreshAccessToken().finally(() => {
      HttpRequest.refreshPromise = null;
    });

    return HttpRequest.refreshPromise;
  }

  /**
   * Calls the auth service's refresh endpoint and stores the new access token
   */
  private static async doRefreshAccessToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    console.info('🔄 Refreshing access token...');

    // CSI-344: a Keycloak session is refreshed at Keycloak's token endpoint (the
    // refresh token is single-use there, so the rotated pair is stored).
    if (isKeycloakSession()) {
      try {
        const tokens = await refreshKeycloakSession(refreshToken);
        storeKeycloakSession(tokens);
        return tokens.accessToken;
      } catch (error) {
        console.error('❌ Error refreshing the Keycloak session:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
        localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        throw error;
      }
    }

    try {
      // Refresh always targets the shared auth service, regardless of which
      // HttpRequest instance's host triggered the token expiry.
      const refreshUrl = `${config.endpoints.auth}/${AUTH_PATHS.REFRESH}`;

      console.info(`📡 Calling refresh endpoint: ${refreshUrl}`);

      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        const { accessToken } = data.data;
        localStorage.setItem('accessToken', accessToken);
        console.info('✅ Token renewed successfully');
        // Note: emitTokenRefresh is called by the calling function
        return accessToken;
      } else {
        const errorText = await response.text();
        console.error(`❌ Refresh failed with status ${response.status}:`, errorText);
        throw new Error(`HTTP ${response.status}: Failed to refresh token`);
      }
    } catch (error) {
      console.error('❌ Error refreshing token:', error);
      // Si el refresh falla, limpiar tokens y forzar re-login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw error;
    }
  }

  /**
   * Check if token is close to expiring: within 5 minutes for a legacy token
   * (3-8 h lifetime), within 1 minute for a Keycloak one (5 min lifetime, so a
   * 5-minute window would refresh on every request).
   */
  private static isTokenNearExpiry(token: string): boolean {
    try {
      const decoded = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - currentTime;
      const threshold = isKeycloakSession() ? 60 : 300;
      return timeUntilExpiry < threshold;
    } catch {
      return false; // Si no se puede decodificar, asumir que no está próximo a expirar
    }
  }
}
